import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { LocationObject } from 'expo-location';
import { useFocusEffect } from 'expo-router';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useRequestLocation from 'shared/hooks/maps/useRequestLocation';
import theme from 'shared/styles/theme';

interface RequestLocationBottomSheetProps {
  onLocation: (location: LocationObject | GeolocationPosition) => unknown;
  onDeniedLocation: (error?: GeolocationPositionError) => unknown;
  title: string;
  body: string;
}

function RequestLocationBottomSheet({
  onLocation,
  onDeniedLocation,
  title,
  body,
}: RequestLocationBottomSheetProps) {
  const { t: translatedText } = useTranslation('maps', {
    keyPrefix: 'requestLocationBottomSheet',
  });
  const requestLocationBottomSheetRef = useRef<BottomSheetModal>(null);

  const { requestLocation, requestLocationPermission } = useRequestLocation(
    requestLocationBottomSheetRef,
    onLocation,
    onDeniedLocation,
  );

  const handleNoThankYouClickedAnalytics = usePressableAnalytics({
    analyticsId: 'noThankYouClicked',
  });

  const handleGoToSettingClickedAnalytics = usePressableAnalytics({
    analyticsId: 'goToSettingsClicked',
  });

  const handleCloseBottomSheetLocationRequest = () => {
    void onDeniedLocation();

    requestLocationBottomSheetRef.current?.dismiss();
    handleNoThankYouClickedAnalytics();
  };

  useFocusEffect(
    useCallback(() => {
      void requestLocation();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  );

  return (
    <BottomSheet
      ref={requestLocationBottomSheetRef}
      onDismiss={handleCloseBottomSheetLocationRequest}
      index={0}
      snapPoints={['50%', '75%']}
    >
      <View style={styles.bottomSheetContainer}>
        <TitleMdText numberOfLines={0}>{title}</TitleMdText>
        <BodyBaseText numberOfLines={0}>{body}</BodyBaseText>

        <Button
          label={translatedText('button.enableLocation')}
          onPress={async () => {
            await requestLocationPermission();
            handleGoToSettingClickedAnalytics();
          }}
          size="LARGE"
          type="PRIMARY"
          width="HUG"
          analyticsId="locationAccepted"
        />

        <Button
          label={translatedText('button.noThankYou')}
          onPress={handleCloseBottomSheetLocationRequest}
          size="LARGE"
          type="SECONDARY"
          width="HUG"
          analyticsId="locationRejected"
        />
      </View>
    </BottomSheet>
  );
}

const styles = StyleSheet.create({
  bottomSheetContainer: {
    gap: theme.spacing.m,
    paddingBottom: theme.spacing.m,
    paddingLeft: theme.spacing.m,
    paddingRight: theme.spacing.m,
  },
});

export default RequestLocationBottomSheet;
